import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, IconButton, Tooltip, Divider,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import CasinoIcon from '@mui/icons-material/Casino';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';
import { fmtDateTime, toInputValue, toApiDatetime } from '../../utils/date';

const getQrUrl = (codigo: string) => {
  const link = `https://pagali.cv/evento/${codigo}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}&margin=10`;
};

const getLandingUrl = (codigo: string) => `https://pagali.cv/evento/${codigo}`;

interface Evento {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  local: string;
  inicio: string;
  fim: string;
  pontos_premio: number;
  estado: 'ATIVO' | 'ENCERRADO' | 'CANCELADO';
  vencedor_user_id: string | null;
  sorteio_em: string | null;
  total_participantes: number;
  na_janela: number;
}

interface Participante {
  id: number;
  user_id: string;
  nome: string;
  email: string;
  registered_at: string;
  na_janela: number;
  vencedor: number;
}

interface Stats {
  total: number;
  na_janela: number;
  vencedores: number;
}

const fmtDate = fmtDateTime;

const EventosPromo: React.FC = () => {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);
  const [participantes, setParticipantes] = useState<Participante[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loadingPart, setLoadingPart] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    codigo: '', nome: '', descricao: '', local: '',
    inicio: '2026-06-06T20:00', fim: '2026-06-07T01:00',
    pontos_premio: 500,
  });

  const [sortearLoading, setSortearLoading] = useState(false);
  const [vencedorResult, setVencedorResult] = useState<{ nome: string; email: string; pontos: number } | null>(null);
  const [sortearConfirm, setSortearConfirm] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [alertMsg, setAlertMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const [qrEvento, setQrEvento] = useState<Evento | null>(null);
  const [editEvento, setEditEvento] = useState<Evento | null>(null);
  const [editForm, setEditForm] = useState({ nome: '', descricao: '', local: '', inicio: '', fim: '', pontos_premio: 500, estado: 'ATIVO' });
  const [saving, setSaving] = useState(false);

  const loadEventos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/eventos-promo');
      setEventos(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEventos(); }, [loadEventos]);

  const loadParticipantes = async (evento: Evento) => {
    setSelectedEvento(evento);
    setParticipantes([]);
    setStats(null);
    setVencedorResult(null);
    setLoadingPart(true);
    try {
      const res = await api.get(`/admin/eventos-promo/${evento.codigo}/participantes`);
      setParticipantes(res.data.data.participantes || []);
      setStats(res.data.data.stats);
    } catch (err: any) {
      setAlertMsg({ type: 'error', msg: err.response?.data?.error || 'Erro ao carregar participantes' });
    } finally {
      setLoadingPart(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/admin/eventos-promo', {
        ...formData,
        inicio: toApiDatetime(formData.inicio),
        fim: toApiDatetime(formData.fim),
      });
      setCreateOpen(false);
      loadEventos();
      // Mostrar QR do evento recém-criado
      const fakeEvento = { ...formData, id: 0, estado: 'ATIVO', vencedor_user_id: null, sorteio_em: null, total_participantes: 0, na_janela: 0 } as Evento;
      setQrEvento(fakeEvento);
    } catch (err: any) {
      setAlertMsg({ type: 'error', msg: err.response?.data?.error || 'Erro ao criar evento' });
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (ev: Evento) => {
    setEditEvento(ev);
    setEditForm({
      nome: ev.nome,
      descricao: ev.descricao || '',
      local: ev.local || '',
      inicio: toInputValue(ev.inicio),
      fim: toInputValue(ev.fim),
      pontos_premio: ev.pontos_premio,
      estado: ev.estado,
    });
  };

  const handleEdit = async () => {
    if (!editEvento) return;
    setSaving(true);
    try {
      await api.put(`/admin/eventos-promo/${editEvento.codigo}`, {
        ...editForm,
        inicio: toApiDatetime(editForm.inicio),
        fim: toApiDatetime(editForm.fim),
      });
      setEditEvento(null);
      setAlertMsg({ type: 'success', msg: 'Evento atualizado com sucesso!' });
      loadEventos();
    } catch (err: any) {
      setAlertMsg({ type: 'error', msg: err.response?.data?.error || 'Erro ao atualizar evento' });
    } finally {
      setSaving(false);
    }
  };

  const downloadQr = (codigo: string) => {
    const link = document.createElement('a');
    link.href = getQrUrl(codigo);
    link.download = `qr-pagali-${codigo}.png`;
    link.target = '_blank';
    link.click();
  };

  const handleSortear = async () => {
    if (!selectedEvento) return;
    setSortearLoading(true);
    setSortearConfirm(false);
    try {
      const res = await api.post(`/admin/eventos-promo/${selectedEvento.codigo}/sortear`);
      setVencedorResult(res.data.data.vencedor ? { ...res.data.data.vencedor, pontos: res.data.data.pontos } : null);
      setAlertMsg({ type: 'success', msg: res.data.message });
      loadEventos();
      loadParticipantes(selectedEvento);
    } catch (err: any) {
      setAlertMsg({ type: 'error', msg: err.response?.data?.error || 'Erro no sorteio' });
    } finally {
      setSortearLoading(false);
    }
  };

  const handleReset = async () => {
    if (!selectedEvento) return;
    setResetConfirm(false);
    try {
      await api.delete(`/admin/eventos-promo/${selectedEvento.codigo}/vencedor`);
      setVencedorResult(null);
      setAlertMsg({ type: 'success', msg: 'Sorteio reiniciado com sucesso.' });
      loadEventos();
      loadParticipantes(selectedEvento);
    } catch (err: any) {
      setAlertMsg({ type: 'error', msg: err.response?.data?.error || 'Erro ao reiniciar sorteio' });
    }
  };

  const copyLink = (codigo: string) => {
    const url = `${window.location.origin.replace('admin.', '')}/evento/${codigo}`;
    navigator.clipboard.writeText(url);
    setAlertMsg({ type: 'success', msg: 'Link copiado!' });
  };

  const estadoColor = (estado: string) => {
    if (estado === 'ATIVO') return 'success';
    if (estado === 'ENCERRADO') return 'default';
    return 'error';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Eventos Promocionais</Typography>
          <Typography variant="body2" color="text.secondary">
            Sorteios em eventos — registo, participantes e atribuição de pontos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadEventos} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Novo Evento
          </Button>
        </Box>
      </Box>

      {alertMsg && (
        <Alert severity={alertMsg.type} onClose={() => setAlertMsg(null)} sx={{ mb: 2 }}>
          {alertMsg.msg}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* Lista de eventos */}
          <Grid item xs={12} md={selectedEvento ? 4 : 12}>
            {eventos.length === 0 ? (
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <EmojiEventsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">Nenhum evento criado ainda.</Typography>
                  <Button sx={{ mt: 2 }} onClick={() => setCreateOpen(true)} startIcon={<AddIcon />}>
                    Criar primeiro evento
                  </Button>
                </CardContent>
              </Card>
            ) : (
              eventos.map((ev) => (
                <Card
                  key={ev.id}
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    cursor: 'pointer',
                    border: selectedEvento?.id === ev.id ? '2px solid #6C3FC5' : undefined,
                    '&:hover': { borderColor: '#6C3FC5' },
                  }}
                  onClick={() => loadParticipantes(ev)}
                >
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip label={ev.estado} color={estadoColor(ev.estado) as any} size="small" />
                          {ev.vencedor_user_id && (
                            <Chip icon={<EmojiEventsIcon />} label="Sorteado" color="warning" size="small" />
                          )}
                        </Box>
                        <Typography fontWeight={700}>{ev.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ev.local} · {fmtDate(ev.inicio)} – {fmtDate(ev.fim)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Typography variant="caption">
                            <PeopleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {ev.na_janela || 0} na janela / {ev.total_participantes || 0} total
                          </Typography>
                          <Typography variant="caption" color="primary">
                            🎁 {ev.pontos_premio} pts
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Ver QR Code">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setQrEvento(ev); }}>
                            <QrCodeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar evento">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(ev); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copiar link">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyLink(ev.codigo); }}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Grid>

          {/* Detalhe do evento */}
          {selectedEvento && (
            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{selectedEvento.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(selectedEvento.inicio)} → {fmtDate(selectedEvento.fim)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedEvento.vencedor_user_id && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => setResetConfirm(true)}
                        >
                          Reiniciar sorteio
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={sortearLoading ? <CircularProgress size={16} color="inherit" /> : <CasinoIcon />}
                        disabled={sortearLoading || !!selectedEvento.vencedor_user_id}
                        onClick={() => setSortearConfirm(true)}
                        sx={{ background: 'linear-gradient(135deg, #6C3FC5, #9B59B6)' }}
                      >
                        Sortear Vencedor
                      </Button>
                    </Box>
                  </Box>

                  {/* Stats */}
                  {stats && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {[
                        { label: 'Total', value: stats.total },
                        { label: 'Na janela (elegíveis)', value: stats.na_janela, color: 'success.main' },
                        { label: 'Vencedor(es)', value: stats.vencedores, color: 'warning.main' },
                      ].map((s) => (
                        <Box key={s.label} sx={{ flex: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight={800} color={s.color || 'text.primary'}>{s.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Resultado do sorteio */}
                  {vencedorResult && (
                    <Alert severity="success" icon={<EmojiEventsIcon />} sx={{ mb: 2 }}>
                      <strong>Vencedor: {vencedorResult.nome}</strong> ({vencedorResult.email})
                      — {vencedorResult.pontos} pontos atribuídos!
                    </Alert>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Tabela participantes */}
                  {loadingPart ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Registo</TableCell>
                            <TableCell>Janela</TableCell>
                            <TableCell>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {participantes.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                                Nenhum participante ainda
                              </TableCell>
                            </TableRow>
                          ) : (
                            participantes.map((p) => (
                              <TableRow
                                key={p.id}
                                sx={{ bgcolor: p.vencedor ? 'warning.50' : undefined }}
                              >
                                <TableCell>{p.nome || '—'}</TableCell>
                                <TableCell>{p.email || '—'}</TableCell>
                                <TableCell>{fmtDate(p.registered_at)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={p.na_janela ? 'Sim' : 'Não'}
                                    color={p.na_janela ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {p.vencedor ? (
                                    <Chip icon={<EmojiEventsIcon />} label="Vencedor" color="warning" size="small" />
                                  ) : '—'}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Dialog: Criar evento */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Evento Promocional</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Código único (ex: cvma2026)"
            value={formData.codigo}
            onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toLowerCase().replace(/\s/g, '') })}
            helperText="Usado no URL da landing page: pagali.cv/evento/{codigo}"
            fullWidth
          />
          <TextField
            label="Nome do evento"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            fullWidth
          />
          <TextField
            label="Local"
            value={formData.local}
            onChange={(e) => setFormData({ ...formData, local: e.target.value })}
            fullWidth
          />
          <TextField
            label="Descrição (opcional)"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Início (janela registo)"
              type="datetime-local"
              value={formData.inicio}
              onChange={(e) => setFormData({ ...formData, inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fim (janela registo)"
              type="datetime-local"
              value={formData.fim}
              onChange={(e) => setFormData({ ...formData, fim: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <TextField
            label="Pontos do prémio"
            type="number"
            value={formData.pontos_premio}
            onChange={(e) => setFormData({ ...formData, pontos_premio: Number(e.target.value) })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !formData.codigo || !formData.nome}
          >
            {creating ? <CircularProgress size={20} /> : 'Criar Evento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: QR Code */}
      <Dialog open={!!qrEvento} onClose={() => setQrEvento(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeIcon /> QR Code — {qrEvento?.nome}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          {qrEvento && (
            <>
              <Box sx={{ display: 'inline-block', p: 1, border: '1px solid #eee', borderRadius: 2, mb: 2 }}>
                <img
                  src={getQrUrl(qrEvento.codigo)}
                  alt="QR Code"
                  width={250}
                  height={250}
                  style={{ display: 'block' }}
                />
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-all' }}>
                {getLandingUrl(qrEvento.codigo)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => { copyLink(qrEvento.codigo); }}>
                  Copiar link
                </Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadQr(qrEvento.codigo)}>
                  Descarregar QR
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrEvento(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar evento */}
      <Dialog open={!!editEvento} onClose={() => setEditEvento(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Evento — {editEvento?.codigo}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Nome do evento"
            value={editForm.nome}
            onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
            fullWidth
          />
          <TextField
            label="Local"
            value={editForm.local}
            onChange={(e) => setEditForm({ ...editForm, local: e.target.value })}
            fullWidth
          />
          <TextField
            label="Descrição"
            value={editForm.descricao}
            onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Início (janela registo)"
              type="datetime-local"
              value={editForm.inicio}
              onChange={(e) => setEditForm({ ...editForm, inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fim (janela registo)"
              type="datetime-local"
              value={editForm.fim}
              onChange={(e) => setEditForm({ ...editForm, fim: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Pontos do prémio"
              type="number"
              value={editForm.pontos_premio}
              onChange={(e) => setEditForm({ ...editForm, pontos_premio: Number(e.target.value) })}
              fullWidth
            />
            <TextField
              label="Estado"
              select
              value={editForm.estado}
              onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="ATIVO">Ativo</option>
              <option value="ENCERRADO">Encerrado</option>
              <option value="CANCELADO">Cancelado</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEvento(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar alterações'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar sorteio */}
      <Dialog open={sortearConfirm} onClose={() => setSortearConfirm(false)}>
        <DialogTitle>Confirmar Sorteio</DialogTitle>
        <DialogContent>
          <Typography>
            Vais sortear aleatoriamente um vencedor entre os{' '}
            <strong>{stats?.na_janela || 0} participantes elegíveis</strong> (dentro da janela do evento).
          </Typography>
          <Typography sx={{ mt: 1 }}>
            O vencedor receberá <strong>{selectedEvento?.pontos_premio || 500} pontos Pagali</strong> automaticamente.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita sem reiniciar o sorteio.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSortearConfirm(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleSortear} startIcon={<CasinoIcon />}>
            Sortear agora
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar reset */}
      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)}>
        <DialogTitle>Reiniciar Sorteio</DialogTitle>
        <DialogContent>
          <Typography>
            Tens a certeza que queres reiniciar o sorteio? O vencedor atual será removido
            (os pontos já atribuídos não serão retirados).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirm(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleReset}>
            Reiniciar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventosPromo;
